import "./header.css";

import React from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";

import { NAV_LINKS, ROUTES, SECTIONS } from "../../data/content";

//Header height - sections scrolled to stop just below it
const OFFSET = 90;

//Works out which home page section is at the top of the window, so its menu item can be highlighted (null = HOME)
const useActiveSection = (isHome) => {
  const [active, setActive] = React.useState(null);

  React.useEffect(() => {
    if (!isHome) {
      setActive(null);
      return undefined;
    }
    const ids = Object.values(SECTIONS);
    const onScroll = () => {
      let current = null;
      ids.forEach((id) => {
        const el = document.getElementById(id);
        if (el && el.getBoundingClientRect().top <= OFFSET + 40) {
          current = id;
        }
      });
      //Contact is short - count it as active once the page is scrolled to the bottom
      if (window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 4) {
        current = SECTIONS.CONTACT;
      }
      setActive(current);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, [isHome]);

  return active;
};

//Orange bar fixed to the top: the menu scrolls to each section of the one-page home page, plus Book A Room (as on Wix).
//On phones the menu opens from a button
const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = React.useState(false);
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const isHome = pathname === ROUTES.HOME;
  const active = useActiveSection(isHome);
  const toggleRef = React.useRef(null);
  const menuRef = React.useRef(null);

  //Close the menu after navigating
  React.useEffect(() => {
    setIsMenuOpen(false);
  }, [pathname]);

  React.useEffect(() => {
    if (!isMenuOpen) {
      return undefined;
    }
    //Stop the page behind scrolling, and move focus into the menu
    document.body.style.overflow = "hidden";
    menuRef.current.querySelector("a").focus();

    const toggle = toggleRef.current;
    const onKeyDown = (e) => {
      if (e.key === "Escape") {
        setIsMenuOpen(false);
        return;
      }
      //Keep Tab inside the menu (and its close button) while it's open
      if (e.key === "Tab") {
        const focusable = [toggle, ...menuRef.current.querySelectorAll("a")];
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        }
        else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.body.style.overflow = "";
      document.removeEventListener("keydown", onKeyDown);
      toggle.focus({ preventScroll: true });
    };
  }, [isMenuOpen]);

  //On the home page the links scroll to their section; from other pages they go to /#section
  const onNavClick = (section) => (e) => {
    setIsMenuOpen(false);
    if (!isHome) {
      return;
    }
    e.preventDefault();
    if (section) {
      document.getElementById(section).scrollIntoView({ behavior: "smooth", block: "start" });
    }
    else {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
    navigate(section ? `/#${section}` : "/", { replace: true, state: { noScroll: true } });
  };

  const bookLink = (className) => (
    <Link
      to={ROUTES.BOOK}
      className={className}
      aria-current={pathname === ROUTES.BOOK ? "page" : undefined}
    >
      Book A Room
    </Link>
  );

  return (
    <header className={`site-header ${isMenuOpen ? "is-menu-open" : ""}`}>
      <div className={"site-header-inner"}>
        {bookLink("site-book is-mobile")}

        <button
          ref={toggleRef}
          type={"button"}
          className={"menu-toggle"}
          aria-expanded={isMenuOpen}
          aria-controls={"site-menu"}
          aria-label={isMenuOpen ? "Close menu" : "Open menu"}
          onClick={() => setIsMenuOpen(!isMenuOpen)}
        >
          <span className={"menu-toggle-bars"} />
        </button>

        <nav
          ref={menuRef}
          id={"site-menu"}
          className={"site-nav"}
          aria-label={"Main"}
        >
          <ul className={"site-nav-list"}>
            {NAV_LINKS.map((link) => {
              const isActive = isHome && active === link.section;
              return (
                <li key={link.label}>
                  <Link
                    to={link.section ? `/#${link.section}` : ROUTES.HOME}
                    className={`site-nav-link ${isActive ? "is-active" : ""}`}
                    aria-current={isActive ? "location" : undefined}
                    onClick={onNavClick(link.section)}
                  >
                    {link.label}
                  </Link>
                </li>
              );
            })}
          </ul>
          {bookLink("site-book")}
        </nav>
      </div>
      <div
        className={"site-menu-backdrop"}
        onClick={() => setIsMenuOpen(false)}
        aria-hidden={"true"}
      />
    </header>
  );
};

export default Header;
